<template>
    <Dialog>
        <DialogTrigger>
            <slot></slot>
        </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>
                    {{ $t("event_types.event_detail.create_time_slot.title") }}
                    <span class="font-bold">
                        {{ formattedDayHeader(day.date) }}
                    </span></DialogTitle
                >
                <DialogDescription>
                    {{
                        $t(
                            "event_types.event_detail.create_time_slot.description",
                        )
                    }}
                </DialogDescription>
            </DialogHeader>
            <form
                class="mt-2 rounded-lg px-1 pb-2 sm:w-full sm:max-w-lg sm:align-middle inline-block transform overflow-hidden text-left align-bottom transition-all"
                @submit.prevent="createEvent"
            >
                <div class="">
                    <div class="">
                        <label
                            for="title"
                            class="text-sm font-medium text-iron-700 block text-left"
                        >
                            {{
                                $t(
                                    "event_types.event_detail.create_time_slot.title_label",
                                )
                            }}*</label
                        >
                        <div class="mt-1 w-full">
                            <input
                                v-model="form.title"
                                name="title"
                                type="text"
                                required
                                class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                                :placeholder="
                                    $t(
                                        'event_types.event_detail.create_time_slot.title_description',
                                    )
                                "
                            />
                        </div>
                    </div>
                    <div class="mt-4">
                        <label
                            for="seats"
                            class="text-sm font-medium text-iron-700 block text-left"
                        >
                            {{
                                $t(
                                    "event_types.event_detail.create_time_slot.seats_label",
                                )
                            }}
                        </label>
                        <div class="mt-1 w-full">
                            <input
                                v-model="form.seats"
                                name="seats"
                                type="number"
                                min="1"
                                required
                                class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                                :placeholder="
                                    $t(
                                        'event_types.event_detail.create_time_slot.seats_description',
                                    )
                                "
                            />
                        </div>
                    </div>
                </div>

                <div class="mb-4 flex justify-between">
                    <div class="mr-6 w-full">
                        <div class="mt-4 w-full">
                            <label
                                for="from"
                                class="text-sm font-medium text-iron-700 block text-left"
                                >{{
                                    $t(
                                        "event_types.event_detail.create_time_slot.from_label",
                                    )
                                }}*</label
                            >
                            <div class="mt-1 w-full">
                                <input
                                    v-model="form.start"
                                    name="from"
                                    type="time"
                                    required
                                    class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="w-full">
                        <div class="mt-4">
                            <label
                                for="to"
                                class="text-sm font-medium text-iron-700 block text-left"
                                >{{
                                    $t(
                                        "event_types.event_detail.create_time_slot.to_label",
                                    )
                                }}*</label
                            >
                            <div class="mt-1 w-full">
                                <input
                                    v-model="form.end"
                                    name="to"
                                    type="time"
                                    required
                                    :min="
                                        form.start
                                            ? form.start
                                            : new Date().toTimeString()
                                    "
                                    class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="gap-4 pt-3 sm:flex sm:flex-row-reverse">
                    <ShadBtn type="submit">
                        {{
                            $t(
                                "event_types.event_detail.create_time_slot.create",
                            )
                        }}
                    </ShadBtn>

                    <DialogClose>
                        <ShadBtn
                            type="button"
                            class="mt-3 rounded-md border-iron-300 bg-white px-4 py-2 text-base font-medium text-iron-700 shadow-sm hover:bg-iron-50 focus:ring-z-orange-500 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm inline-flex w-full justify-center border focus:ring-2 focus:ring-offset-2 focus:outline-none"
                        >
                            {{ $t("cancel") }}
                        </ShadBtn>
                    </DialogClose>
                </div>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogClose,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { Button as ShadBtn } from "@/components/ui/button";
import { router } from "@inertiajs/vue3";
import { reactive } from "vue";

const props = defineProps<{
    day: {
        date: Date;
    };
    eventTypeId: number;
}>();

const emit = defineEmits(["refreshEvents"]);

const form = reactive({
    title: "",
    start: null,
    end: null,
    recurrence: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    seats: 1,
});

async function createEvent() {
    const until = getFormattedFromAndUntilForForm();

    await router.post(
        `/event-types/${props.eventTypeId}/events`,
        {
            title: form.title,
            start: form.start,
            end: form.end,
            type: "call",
            from: getFormattedFromAndUntilForForm(),
            until: until,
            recurrence: form.recurrence,
            timezone: form.timezone,
            seats: form.seats,
        },
        {
            onSuccess: () => {
                emit("refreshEvents");
            },
        },
    );
}

function formattedDayHeader(date: Date) {
    return format(date, "do MMMM");
}

function getFormattedFromAndUntilForForm() {
    if (props.day) {
        return format(props.day.date, "yyyy-MM-dd");
    }
}
</script>
